import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  Activity,
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  Clock,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minimize2,
  Minus,
  Monitor,
  MoveHorizontal,
  PersonStanding,
  Plus,
  Scale,
  Sparkles,
  Zap,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/thigh-lift-surgery-mumbai";

const THIGH_LIFT_FAQS = [
  {
    question: "Will exercise remove loose thigh skin?",
    answer:
      "No. Exercise builds the underlying muscle but does not address skin laxity. Once thigh skin has lost its elasticity, whether from weight loss, aging, or genetics, it does not retract through exercise. Thighplasty removes the excess skin that cannot be corrected non-surgically.",
  },
  {
    question: "How visible are thigh lift scars?",
    answer:
      "For the inner thigh lift, the scar sits in the groin crease and is concealed by underwear and swimwear. It is not visible when the legs are together. The vertical thigh lift adds a scar running down the inner thigh which is more visible. The decision to use the vertical technique is made only when the degree of skin excess cannot be adequately corrected through the groin approach alone. All scars fade over 12 to 18 months.",
  },
  {
    question: "Can thigh lift be combined with other procedures?",
    answer:
      "Yes. Thigh lift is commonly combined with tummy tuck, arm lift, and body lift as part of a comprehensive post-weight loss body contouring plan. Combining procedures reduces the total number of surgeries and recoveries. The combination is planned based on what the patient's health and surgical duration safely allow in a single session.",
  },
  {
    question: "How long does thigh lift recovery take?",
    answer:
      "Most patients return to desk work within 3 to 4 weeks. Full activity including lower body exercise resumes after 6 weeks. Final thigh contour is visible at approximately 3 months when all residual swelling has resolved. Scar maturation continues over 12 to 18 months.",
  },
  {
    question: "Does thigh lift reduce the friction and irritation from thighs rubbing?",
    answer:
      "Yes. Removing the excess inner thigh skin that causes rubbing eliminates the mechanical cause of skin friction, irritation, and rashes in the inner thigh crease. Many patients describe this functional improvement as equally or more significant than the cosmetic outcome.",
  },
  {
    question: "Are thigh lift results permanent?",
    answer:
      "The skin removed is permanently gone. The result is long-lasting provided weight remains stable. Significant weight gain adds volume to the remaining thigh tissue and can stretch the corrected skin. Aging continues to affect skin quality after surgery, but the structural improvement from the lift is maintained over the long term.",
  },
  {
    question: "Is there a non-surgical alternative to thigh lift?",
    answer:
      "Energy-based devices including radiofrequency and ultrasound can produce modest skin tightening in patients with mild, early laxity. They do not produce results comparable to surgical skin removal in patients with established skin excess. For patients with significant hanging thigh skin, non-surgical treatments produce insufficient correction to be a meaningful alternative.",
  },
  {
    question: "What are the risks of thigh lift surgery?",
    answer:
      "Swelling, bruising, and temporary numbness along the inner thigh are expected during recovery. Seroma, wound healing delays, scar widening, and infection are possible. The inner thigh incision is in a location under repeated tension from leg movement and sitting, which increases the risk of wound complications compared to incisions in less mobile areas. Smoking significantly increases this risk. Compression garment use, activity restriction during healing, and careful positioning during the first 2 weeks all reduce complication risk.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Thigh lift",
      alternateName: ["Thighplasty", "Medial thigh lift"],
      description:
        "Surgical excision of excess thigh skin and fat, often with liposuction, for contour improvement after weight loss, aging, or skin laxity.",
      procedureType: "surgical",
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Thigh" },
      howPerformed:
        "Marking upright and in functional positions; liposuction when indicated; skin excision via groin and/or vertical medial thigh approach; layered closure; compression garments; general anesthesia; 2–4 hours; same-day or overnight stay depending on extent.",
      url: PAGE_URL,
      additionalProperty: [
        { "@type": "PropertyValue", name: "Procedure category", value: "Surgical body contouring" },
        { "@type": "PropertyValue", name: "Common combinations", value: "Liposuction, post–weight loss body contouring" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: THIGH_LIFT_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function ThighLiftJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Body", href: "/#services" },
    { name: "Thigh Lift", href: "/thigh-lift-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Body</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Thigh Lift Surgery
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Thighplasty by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Award className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">Board Certified</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Clock className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">17+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <GraduationCap className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">International Training</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Thigh Lift" icon="🦵" size="hero"  imageSrc={HERO.thighLift} imageAlt="Thigh lift"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">What is Thigh Lift Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A thigh lift, clinically called thighplasty, removes excess skin and fat from the thighs to produce firmer, smoother, and
              more proportionate legs. It corrects loose, sagging skin that develops after significant weight loss, with aging, or from
              genetics, and does not improve with exercise or diet. Building thigh muscle beneath loose skin improves strength and tone
              but does not address the skin itself. Surgical removal is the only effective correction for skin excess.
            </p>
            <p>
              The distinction between a fat problem and a skin problem determines the appropriate treatment. Thigh fat in a patient with
              good skin elasticity responds to liposuction alone. Skin laxity with or without excess fat requires thighplasty. Most
              patients presenting for thigh contouring after weight loss have a combination of both, making thigh lift with simultaneous
              liposuction the most common approach. Dr. Jain assesses skin elasticity and fat distribution during consultation to
              determine which component dominates and which technique is appropriate.
            </p>
            <p>
              Dr. Vicky Jain performs thigh lift surgery as a standalone procedure and as part of comprehensive post-weight
              loss body contouring plans alongside tummy tuck, arm lift, and body lift procedures. The procedure takes 2 to 4 hours and
              most patients return to light activity within 2 weeks.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Thighplasty</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Thigh lift surgery involves a specific scar trade-off that varies by technique. The inner thigh lift places the scar in
                the groin crease where it is concealed by underwear and swimwear and not visible when the legs are together. The vertical
                thigh lift, required for more severe skin excess, adds a scar running down the inner thigh from the groin toward the
                knee. This vertical scar is more visible than the groin scar and the decision to use this technique is made only when
                the degree of skin excess cannot be adequately corrected through the groin crease incision alone.
              </p>
              <p>
                Dr. Jain marks the planned incision and the skin to be removed with the patient standing before surgery, assessing how
                the inner thigh skin behaves in both standing and sitting positions. The amount of skin removed determines the tightness
                of the final result and the tension on the closure. Excessive tension widens scars and increases wound healing
                complications in this location. The calibration between adequate correction and safe closure tension is one of the most
                important technical judgments in this procedure.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61,37,102,0) 20%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatCorrectsSection() {
  const cards = [
    {
      title: "Loose Inner Thigh Skin",
      icon: Layers,
      copy: "Skin that hangs from the inner thigh, creates the appearance of sagging between the legs, and rubs together during walking. Inner thigh skin excess is the most common presentation for thighplasty and the primary driver of both cosmetic and functional concerns.",
    },
    {
      title: "Post-Weight Loss Thigh Skin",
      icon: Scale,
      copy: "Significant weight loss removes the fat that was supporting the thigh skin. The excess skin cannot retract and hangs from the inner and sometimes outer thigh. The degree of excess is proportional to how much weight was lost and the patient's skin elasticity.",
    },
    {
      title: "Skin Friction and Irritation",
      icon: Zap,
      copy: "Loose inner thigh skin that rubs together during walking creates chronic skin irritation, rashes, and in some cases skin breakdown in the inner thigh crease. This is a functional problem that goes beyond cosmetic concern and is a valid medical indication for thigh lift surgery.",
    },
    {
      title: "Age-Related Thigh Laxity",
      icon: Sparkles,
      copy: "Thigh skin loses elasticity with age and the underlying fat shifts, creating loose, soft skin on the inner and anterior thigh. Exercise does not restore skin elasticity that has been lost through aging.",
    },
    {
      title: "Outer Thigh and Saddlebag Area",
      icon: MoveHorizontal,
      copy: "Fullness along the outer thigh that creates a wide lower body silhouette. The outer thigh is treated with liposuction when fat is the dominant concern and good skin elasticity is present, or with a lateral thigh lift when skin laxity accompanies the outer thigh fullness.",
    },
    {
      title: "Post-Bariatric Surgery Skin Excess",
      icon: Activity,
      copy: "Patients after bariatric procedures frequently develop significant thigh skin excess as part of widespread post-weight loss body changes. Thigh lift is commonly performed as one component of a staged post-bariatric body contouring plan.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-corrects-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Indications</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What Thigh Lift Surgery Corrects</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed text-sm md:text-base">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PROCEDURE_TYPES = [
  {
    title: "Inner Thigh Lift (Medial Thighplasty)",
    body:
      "The most commonly performed technique. The incision is placed in the natural groin crease where it is concealed by underwear and swimwear. Skin is removed from the upper inner thigh and the remaining skin is tightened upward. Appropriate for mild to moderate inner thigh skin excess where the concern is concentrated in the upper inner thigh and the groin crease incision allows adequate correction.",
  },
  {
    title: "Vertical Thigh Lift",
    body:
      "Used for more significant skin excess that extends down the inner thigh beyond what the groin incision can correct. The incision runs from the groin crease down the inner thigh toward the knee, allowing removal of skin along the full length of the inner thigh. Produces a more visible scar than the medial technique but provides a degree of correction not achievable through the groin approach alone.",
  },
  {
    title: "Outer Thigh Lift",
    body:
      "Addresses skin laxity along the outer thigh and upper buttock area. The incision is placed along the upper outer thigh within the hip and swimsuit line. Frequently performed alongside a body lift or lower body lift for patients with circumferential skin excess after massive weight loss.",
  },
  {
    title: "Circumferential Thigh Lift",
    body:
      "A 360-degree approach addressing the inner thigh, outer thigh, and buttock area in a single procedure. Used for patients after massive weight loss where thigh skin excess is present around the full circumference of the upper leg rather than isolated to the inner surface.",
  },
  {
    title: "Thigh Lift with Liposuction",
    body:
      "Liposuction is performed before skin excision in most thigh lift procedures to remove the fatty component, reduce tissue bulk, and improve the final contour. The combination produces a more completely contoured result than skin removal alone. Used as the default approach for patients with both skin excess and thigh fat.",
  },
];

function TypesSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Procedure Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white">Types of Thigh Lift Surgery</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {PROCEDURE_TYPES.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgerySection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">How Thigh Lift Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 2 to 4 hours under general anesthesia depending on the extent of correction and whether multiple thigh areas
              are treated simultaneously. Dr. Jain marks the planned incision and the skin to be removed with the patient standing
              upright before surgery, assessing skin distribution in standing, sitting, and walking positions.
            </p>
            <p>
              When liposuction is being performed alongside the lift, this is completed first. Removing the fat component before skin
              excision allows a more accurate assessment of the remaining skin excess. The skin is then excised through the planned
              incision, the deep tissue is closed in layers to reduce tension on the skin closure, and the skin is sutured with fine
              sutures. Compression garments are applied immediately after surgery.
            </p>
            <p>
              Most patients go home the same day or after one night depending on the extent of the procedure. Walking is encouraged
              within the first few days to reduce the risk of deep vein thrombosis, though mobility is limited by swelling and
              discomfort during the first week. Sitting applies tension across the inner thigh incision and patients are advised on
              positioning during the first 2 weeks to protect the healing closure.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Week 1",
      description:
        "Swelling, bruising, and tightness along the inner thighs. Compression garment worn continuously. Walking encouraged but limited by discomfort. Sitting positions adjusted to reduce tension on the inner thigh incision.",
      icon: BedDouble,
    },
    {
      period: "Weeks 2 to 3",
      description:
        "Light daily activity resumes. Desk work manageable. Compression garment continues during the day. Swelling reduces and the thigh contour improvement becomes visible.",
      icon: Monitor,
    },
    {
      period: "Weeks 3 to 4",
      description:
        "Most patients return to normal routine. Strenuous activity and lower body exercise remain restricted. The incision is healing and the compression garment supports the result.",
      icon: PersonStanding,
    },
    {
      period: "Weeks 6 to 8 and Beyond",
      description:
        "Full activity including gym resumes. Final thigh contour is visible as all swelling resolves. Scar maturation continues over 12 to 18 months.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const VS_ROWS = [
  {
    label: "Skin Removal",
    lift: "Yes. Excess thigh skin is surgically excised.",
    lipo: "No. Skin must contract on its own after fat removal.",
  },
  {
    label: "Fat Removal",
    lift: "Yes, combined with liposuction in most cases.",
    lipo: "Yes, the primary function.",
  },
  {
    label: "Scar",
    lift: "Scar in groin crease for inner thigh lift, or vertical scar for more extensive correction.",
    lipo: "Small access points only, essentially invisible.",
  },
  {
    label: "Functional Benefit",
    lift: "Yes. Reduces skin friction and irritation from inner thigh rubbing.",
    lipo: "Limited. Does not address skin-related friction.",
  },
  {
    label: "Best For",
    lift: "Patients with skin excess after weight loss, aging, or pregnancy.",
    lipo: "Patients with thigh fat and good skin elasticity who do not have skin excess.",
  },
];

function ThighLiftVsLipoSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="thigh-lift-vs-lipo-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Understanding Your Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">
            Thigh Lift vs. Thigh Liposuction: Which Do You Need?
          </h2>
        </div>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs sm:text-sm md:text-base min-w-[640px]">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-3 pr-2 sm:pr-4 font-serif text-premium-purple font-normal w-[22%]" />
                <th scope="col" className="py-3 px-2 sm:px-3 font-serif text-premium-purple bg-white/80">
                  Thigh Lift
                </th>
                <th scope="col" className="py-3 pl-2 sm:pl-3 font-serif text-premium-purple bg-white/80">
                  Thigh Liposuction
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-3 pr-2 sm:pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-3 px-2 sm:px-3 text-deep-navy/70 align-top">{row.lift}</td>
                  <td className="py-3 pl-2 sm:pl-3 text-deep-navy/70 align-top">{row.lipo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center leading-relaxed text-sm md:text-base">
          Performing liposuction on a thigh with poor skin elasticity and existing laxity reduces the fat supporting the skin without
          addressing the skin itself. The result is a thigh with less volume but more apparent laxity. Skin elasticity assessment before
          any thigh contouring procedure determines which approach correctly addresses the problem.
        </p>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
            Who Is a Good Candidate for Thigh Lift Surgery?
          </h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Good candidates are adults with thigh skin excess that does not improve with exercise, who are at a stable weight, and
              are in good general health. The most common presentations are patients after bariatric surgery or significant weight loss,
              patients in their 40s and beyond with age-related thigh laxity, and patients with chronic inner thigh friction and
              irritation that affects their daily activity and clothing choices. There is no upper age limit for patients in good
              health.
            </p>
            <p>
              Stable weight before surgery is essential. Patients who are still losing weight should complete their weight loss before
              thigh lift because additional weight loss creates new skin excess in the corrected area. Patients planning significant
              weight loss after surgery are counseled that the surgical result may be affected. Non-smokers or patients willing to stop
              at least 4 weeks before surgery have significantly better wound healing outcomes. The inner thigh location is under
              cyclical tension from leg movement and sitting, making smoking cessation particularly important for reducing wound healing
              complications in this specific procedure. Patients with diabetes or conditions affecting wound healing are assessed
              carefully before surgery.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const PatientResultsSection = () => <PatientTestimonials procedureName="Thigh Lift" />;

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Common Questions</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Frequently Asked Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {THIGH_LIFT_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`thigh-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Thigh Lift Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your thigh skin laxity, fat distribution, and skin elasticity to determine whether
          thigh lift, liposuction, or a combination is appropriate for your anatomy and goals. You leave with a clear understanding of
          which technique applies to your specific concern, what the scar trade-off involves, and what recovery looks like at each
          stage.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Tummy Tuck",
      description: "Address abdominal skin excess alongside thigh correction as part of complete lower body contouring.",
      href: "/tummy-tuck-surgery-mumbai",
    },
    {
      name: "Arm Lift",
      description: "Complete post-weight loss body contouring addressing upper and lower body skin excess together.",
      href: "/arm-lift-surgery-mumbai",
    },
    {
      name: "Liposuction",
      description: "Fat removal from thighs and other areas for patients with fat-only fullness and good skin elasticity.",
      href: "/liposuction-surgery-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-10 md:py-14 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Related Procedures</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Related Procedures</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ThighLiftPage() {
  return (
    <>
      <SEO
        title="Thigh Lift Surgery | Thighplasty by Dr. Vicky Jain"
        description="Loose thigh skin from weight loss or aging that exercise cannot fix. Dr. Vicky Jain performs thigh lift surgery with lasting results. Book your consultation."
        canonical="/thigh-lift-surgery-mumbai"
      />
      <ThighLiftJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <WhatCorrectsSection />
      <TypesSection />
      <HowSurgerySection />
      <RecoveryTimelineSection />
      <ThighLiftVsLipoSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
